import { useEffect, useState } from 'react'
import { RootState } from 'store/'
import { useAppDispatch } from 'store/hooks'
import { actions as searchActions } from 'store/search'
import { ReportDataRequest, actions as dashboardActions } from 'store/dashboard'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import useInterval from 'use-interval'
import Spinner from 'components/spinner/Spinner'

import Report from './report/Report'
import { Box } from '@mui/material'
import theme from 'containers/app/theme'
import MainTitleSection from 'containers/dashboard/singleReportPage/components/MainTitleSection'
import ReportLoading from 'containers/dashboard/singleReportPage/components/ReportLoading'
import ReportError from 'containers/dashboard/singleReportPage/components/ReportError'

type ParamsType = {
  id: string
}

export default function SingleReportPage() {
  const dispatch = useAppDispatch()
  const roles = useSelector((state: RootState) => state.account.roles)
  const searchStatus = useSelector((state: RootState) => state.search.searchStatus)
  const searchError = useSelector((state: RootState) => state.search.error)
  const reportData = useSelector((state: RootState) => state.dashboard.reportData)
  const reportDataLoading = useSelector((state: RootState) => state.dashboard.loading.reportData)
  const reportDataError = useSelector((state: RootState) => state.dashboard.error.reportData)
  const [regeneratingStarted, setRegeneratingStarted] = useState<boolean>(false)
  const params = useParams<ParamsType>()
  const searchId = params?.id || ''
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const limit = queryParams.has('limit') ? parseInt(queryParams.get('limit')!) : null

  useEffect(() => {
    dispatch(searchActions.getSearchStatus(searchId))
  }, [dispatch, searchId])

  useEffect(() => {
    const getReportData = () => {
      const data: ReportDataRequest = {
        id: searchId,
        ...(limit && { limit: limit }),
      }
      dispatch(dashboardActions.fetchReportData(data))
    }

    if (searchStatus && searchStatus !== 'report_compiling') {
      getReportData()
      setRegeneratingStarted(false)
    }
  }, [dispatch, searchStatus, limit, searchId])

  useInterval(
    () => {
      dispatch(searchActions.getSearchStatus(searchId))
    },
    searchStatus === 'report_compiling' || searchStatus === 'search_finished' || regeneratingStarted ? 6000 : null
  )

  const isAdmin = roles?.includes('bo_admin')
  const reportStatusInProgress = searchStatus === 'search_finished' || searchStatus === 'report_compiling'

  if (reportStatusInProgress) {
    return (
      <Box sx={{ py: 3, px: 6 }}>
        <ReportLoading status={searchStatus} isAdmin={isAdmin} searchId={searchId} />
      </Box>
    )
  }

  if (reportDataLoading) {
    return <Spinner size={80} />
  }

  if (searchError.searchStatus) {
    return (
      <ReportError message={searchError.searchStatus} error={searchError.searchStatus} isAdmin={isAdmin} searchId={searchId} />
    )
  }

  if (reportDataError) {
    return (
      <ReportError
        message="Something went wrong while generating the report. Please, contact support."
        error={reportDataError}
        isAdmin={isAdmin}
        searchId={searchId}
      />
    )
  }

  if (reportData && reportData.error) {
    return <ReportError message={reportData.error} error={reportData.error} isAdmin={isAdmin} searchId={searchId} />
  }

  return (
    <Box display="flex" flexDirection="column" sx={{ py: 3, px: 6, backgroundColor: theme.palette.reportBg.main }}>
      {limit && <div>Limit: {limit}</div>}
      {reportData && (
        <>
          <MainTitleSection searchId={searchId} searchInfo={reportData?.search_info} />
          <Report reportData={reportData} />
        </>
      )}
    </Box>
  )
}
